/**
 * CSV Export Utilities
 * Functions for exporting trade data to CSV format
 */

import { Trade } from "../types/trade";
import { formatTradeDateTimeCsvInZone } from "./displayDateTime";

/**
 * Convert a trade object to CSV row
 */
const tradeToCSVRow = (trade: Trade, displayTimeZone: string): string => {
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return "";
    return formatTradeDateTimeCsvInZone(date, displayTimeZone).split("T")[0] || "";
  };

  const formatDateTime = (date: Date | string | undefined): string =>
    formatTradeDateTimeCsvInZone(date, displayTimeZone);

  const formatArray = (arr: string[] | undefined): string => {
    if (!arr || arr.length === 0) return "";
    return arr.join("; ");
  };

  return [
    trade.id,
    trade.symbol,
    trade.type,
    trade.status,
    formatDateTime(trade.entryDate),
    trade.entryPrice.toString(),
    trade.entryQuantity.toString(),
    (trade.entryFees || 0).toString(),
    formatDateTime(trade.exitDate),
    (trade.exitPrice || "").toString(),
    (trade.exitQuantity || "").toString(),
    (trade.exitFees || 0).toString(),
    trade.entryValue.toString(),
    (trade.exitValue || "").toString(),
    (trade.grossPnL || "").toString(),
    (trade.netPnL || "").toString(),
    (trade.returnPercent || "").toString(),
    (trade.roi || "").toString(),
    trade.strategy || "",
    trade.notes || "",
    formatArray(trade.tags),
    formatDate(trade.createdAt),
    formatDate(trade.updatedAt),
  ]
    .map((field) => {
      // Escape commas and quotes in CSV
      const str = String(field);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    })
    .join(",");
};

/**
 * Get CSV header row
 */
const getCSVHeader = (): string => {
  return [
    "ID",
    "Symbol",
    "Type",
    "Status",
    "Entry Date/Time",
    "Entry Price",
    "Entry Quantity",
    "Entry Fees",
    "Exit Date/Time",
    "Exit Price",
    "Exit Quantity",
    "Exit Fees",
    "Entry Value",
    "Exit Value",
    "Gross P&L",
    "Net P&L",
    "Return %",
    "ROI",
    "Strategy",
    "Notes",
    "Tags",
    "Created At",
    "Updated At",
  ].join(",");
};

export interface ExportTradesToCSVOptions {
  /** IANA zone from Display Preferences (defaults to device). */
  displayTimeZone?: string;
}

/**
 * Export trades to CSV file
 */
export const exportTradesToCSV = (
  trades: Trade[],
  filename: string = "trades-export.csv",
  options?: ExportTradesToCSVOptions
): void => {
  if (trades.length === 0) {
    throw new Error("No trades to export");
  }

  const tz =
    options?.displayTimeZone?.trim() ||
    (typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
      : "UTC");
  const header = getCSVHeader();
  const rows = trades.map((t) => tradeToCSVRow(t, tz));
  const csvContent = [header, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
};

/**
 * Generate filename with timestamp
 */
export const generateExportFilename = (): string => {
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 10).replace(/-/g, "");
  return `trades-export-${timestamp}.csv`;
};

